"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import LabOrderDetail from "@/components/lab/lab-order-detail";
import { Button } from "@/components/ui";
import Badge from "@/components/ui/badge";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMarkLabResultsViewed } from "@/hooks/use-mark-lab-results-viewed";
import { formatDate } from "@/lib/format-data";
import { countOutOfRange, sortBiomarkers } from "@/lib/lab-biomarker-range";
import { getLabStatusMeta } from "@/lib/lab-status";
import BiomarkerRangeRow from "./biomarker-range-row";
import { SectionLoadError } from "./section-load-error";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { labQueryOptions } from "@/api/provider/lab/lab-query";

/** The one window this section opens: the biomarker levels, a lab order, or an order reached from the levels. */
interface OpenDialog {
  biomarkers?: boolean;
  orderId?: string;
}
export default function LabsSection(t0) {
  const $ = _c(52);
  const {
    patientId
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = labQueryOptions.patientOrderPages(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isPending,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    isRefetching,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery(t1);
  let t2;
  if ($[2] !== patientId) {
    t2 = labQueryOptions.biomarkers(patientId);
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const biomarkerQuery = useQuery(t2);
  const markResultsViewed = useMarkLabResultsViewed(patientId);
  const [dialog, setDialog] = useState(null);
  let t3;
  if ($[4] !== biomarkerQuery.data) {
    t3 = biomarkerQuery.data ?? [];
    $[4] = biomarkerQuery.data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = sortBiomarkers(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const biomarkers = t4;
  if (isPending || biomarkerQuery.isPending) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <PatientSidebarSectionSkeleton />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (isError && !isFetchNextPageError) {
    let t5;
    if ($[9] !== refetch) {
      t5 = () => refetch();
      $[9] = refetch;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== isRefetching || $[12] !== t5) {
      t6 = <SectionLoadError label="lab orders" onRetry={t5} isRetrying={isRefetching} />;
      $[11] = isRefetching;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  const t5 = data.pages;
  let openOrder;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[14] !== biomarkerQuery || $[15] !== biomarkers || $[16] !== data.pages || $[17] !== markResultsViewed) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const orders = t5.flatMap(_temp);
      if (orders.length === 0) {
        let t11;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <p className="text-caption text-muted-foreground">No lab orders</p>;
          $[24] = t11;
        } else {
          t11 = $[24];
        }
        t10 = t11;
        break bb0;
      }
      openOrder = (orderId, t11) => {
        const fromBiomarkers = t11 === undefined ? false : t11;
        const order = orders.find(o => o.id === orderId);
        if (order) {
          markResultsViewed(order);
        }
        setDialog({
          orderId,
          biomarkers: fromBiomarkers
        });
      };
      const outOfRange = countOutOfRange(biomarkers);
      if ($[25] !== biomarkerQuery || $[26] !== biomarkers || $[27] !== outOfRange) {
        t8 = biomarkerQuery.isError ? <SectionLoadError label="lab results" onRetry={() => biomarkerQuery.refetch()} isRetrying={biomarkerQuery.isRefetching} /> : biomarkers.length > 0 && <button type="button" className="mb-3 flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-left hover:bg-accent/50" onClick={() => setDialog({
          biomarkers: true
        })}><span className="text-body font-medium text-foreground">Biomarkers</span><span className="rounded-full bg-muted px-2 py-0.5 text-caption text-foreground-secondary">{biomarkers.length}</span>{outOfRange > 0 && <Badge size="xs" variant="warning">{outOfRange} out of range</Badge>}<FiChevronRight className="ml-auto size-4 text-foreground-secondary" /></button>;
        $[25] = biomarkerQuery;
        $[26] = biomarkers;
        $[27] = outOfRange;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <p className="px-2 pb-1 text-caption font-medium text-foreground-secondary">Orders</p>;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      t6 = "space-y-1";
      t7 = orders.map(order_0 => {
        const meta = getLabStatusMeta(order_0);
        return <li key={order_0.id}><button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-accent/50" onClick={() => openOrder(order_0.id)}><div className="flex items-center justify-between gap-2"><span className="truncate text-body font-medium text-foreground">{order_0.items.map(_temp2).join(", ") || "Lab order"}</span><Badge size="xs" variant={meta.badgeVariant}>{meta.label}</Badge></div><p className="truncate text-caption text-foreground-secondary">{[order_0.labName || order_0.labSlug, order_0.createdDate && formatDate(order_0.createdDate)].filter(Boolean).join(" \xB7 ")}</p></button></li>;
      });
    }
    $[14] = biomarkerQuery;
    $[15] = biomarkers;
    $[16] = data.pages;
    $[17] = markResultsViewed;
    $[18] = openOrder;
    $[19] = t10;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    openOrder = $[18];
    t10 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[30] !== t6 || $[31] !== t7) {
    t11 = <ul className={t6}>{t7}</ul>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== fetchNextPage || $[34] !== hasNextPage || $[35] !== isFetchNextPageError || $[36] !== isFetchingNextPage) {
    t12 = isFetchNextPageError ? <SectionLoadError label="more lab orders" onRetry={() => fetchNextPage()} isRetrying={isFetchingNextPage} /> : hasNextPage && <Button variant="ghost" className="mt-1 p-1 text-primary" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>Load more</Button>;
    $[33] = fetchNextPage;
    $[34] = hasNextPage;
    $[35] = isFetchNextPageError;
    $[36] = isFetchingNextPage;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const t13 = !!dialog;
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = open => !open && setDialog(null);
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== biomarkers || $[40] !== dialog || $[41] !== openOrder) {
    t15 = <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">{dialog?.orderId ? <><DialogHeader><DialogTitle className="sr-only">Lab order</DialogTitle></DialogHeader><DialogBody><LabOrderDetail orderId={dialog.orderId} onBack={dialog.biomarkers ? () => setDialog({
            biomarkers: true
          }) : undefined} backLabel="Back to biomarkers" /></DialogBody></> : <><DialogHeader><DialogTitle>Biomarkers</DialogTitle><DialogDescription>Each result against its lab's reference range. The newest is colored; older results fade with age. Click a dot to open its lab.</DialogDescription></DialogHeader><DialogBody><div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">{biomarkers.map(history => <BiomarkerRangeRow key={history.key} history={history} onOpenResult={result => openOrder(result.labOrderId, true)} />)}</div></DialogBody></>}</DialogContent>;
    $[39] = biomarkers;
    $[40] = dialog;
    $[41] = openOrder;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t13 || $[44] !== t15) {
    t16 = <Dialog open={t13} onOpenChange={t14}>{t15}</Dialog>;
    $[43] = t13;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t16 || $[49] !== t8 || $[50] !== t9) {
    t17 = <>{t8}{t9}{t11}{t12}{t16}</>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t16;
    $[49] = t8;
    $[50] = t9;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}
function _temp2(item) {
  return item.displayName || "Test";
}
function _temp(page) {
  return page?.data ?? [];
}